/**
 * 实时轨迹会话 / 在线队员 / 点位合并（从 Dashboard 拆出）
 */
import { ONLINE_TIMEOUT } from './constants';
import { readLastAppliedTrackModeConfig } from './dashboardSettingsHelpers';
import {
  buildNormalizedTrackPoint,
  hasMovedSignificantly,
  createTrackSessionInfo,
  clampTrackRefreshIntervalMs
} from './trackDrawingOnMap';

export function endTrackSession(memberName) {
  const key = `track_session_${memberName}`;
  const sessionInfo = localStorage.getItem(key);
  if (!sessionInfo) return null;
  try {
    const session = JSON.parse(sessionInfo);
    console.log(`🔚 结束轨迹会话: ${session.sessionId}`);
    localStorage.removeItem(key);
    return session;
  } catch (error) {
    console.error('结束轨迹会话失败:', error);
    localStorage.removeItem(key);
    return null;
  }
}

export function listOnlineMembers(members = [], now = Date.now(), timeout = ONLINE_TIMEOUT) {
  return (members || []).filter((member) => {
    if (!member.last_seen) return false;
    const lastSeenTime = new Date(member.last_seen).getTime();
    return !Number.isNaN(lastSeenTime) && (now - lastSeenTime) < timeout;
  });
}

export function limitTrackPoints(points = [], maxPoints = 1000) {
  if (!Array.isArray(points)) return [];
  if (points.length <= maxPoints) return points;
  return points.slice(-maxPoints);
}

/**
 * 将新点合并进实时轨迹状态（纯函数）
 * @returns {{ nextState: object, added: boolean, newPoint: object|null }}
 */
export function mergeRealtimeTrackPoint(prevState, memberName, member, options = {}) {
  const { requireSignificantMove = true } = options;
  const existingData = prevState[memberName] || [];
  const lastPoint = existingData[existingData.length - 1];
  const newPoint = buildNormalizedTrackPoint(member);
  if (!newPoint) {
    return { nextState: prevState, added: false, newPoint: null };
  }
  if (requireSignificantMove && !hasMovedSignificantly(lastPoint, newPoint)) {
    return { nextState: prevState, added: false, newPoint };
  }
  const nextPoints = limitTrackPoints([...existingData, newPoint]);
  return {
    nextState: {
      ...prevState,
      [memberName]: nextPoints
    },
    added: true,
    newPoint
  };
}

export function clearMemberPositionWatchers(members = []) {
  (members || []).forEach((member) => {
    if (member.positionWatcher) {
      clearInterval(member.positionWatcher);
      member.positionWatcher = null;
    }
  });
}

/**
 * 创建位置轮询定时器
 */
export function createRealtimePositionWatcher(options = {}) {
  const {
    getMember,
    memberName,
    sessionId,
    intervalMs,
    onPointAdded,
    savePoint
  } = options;

  return setInterval(async () => {
    const currentMember = getMember?.();
    if (!(currentMember && currentMember.last_latitude && currentMember.last_longitude)) return;

    // 由调用方在 setState 回调中使用 mergeRealtimeTrackPoint
    onPointAdded?.({
      memberName,
      member: currentMember,
      sessionId,
      savePoint
    });
  }, intervalMs);
}

/** 创建并持久化轨迹会话 */
export function beginRealtimeTrackSession(memberName) {
  const sessionInfo = createTrackSessionInfo(memberName);
  try {
    localStorage.setItem(`track_session_${memberName}`, JSON.stringify(sessionInfo));
  } catch (_) {
    /* ignore */
  }
  return sessionInfo;
}

/** 需要自动开启实时绘制的在线队员 */
export function listOnlineMembersNeedingTrackDrawing(members = [], drawingSet = new Set()) {
  return listOnlineMembers(members).filter((m) => !drawingSet.has(m.name));
}

/**
 * 启动单队员实时轨迹：首点 + 轮询 watcher
 */
export async function bootstrapMemberTrackDrawing(options = {}) {
  const {
    member,
    sessionId = null,
    getCurrentMember,
    saveInitialPoint,
    onInitialPoint,
    onDrawLine,
    refreshIntervalMs,
    getMember,
    onPointAdded,
    savePoint
  } = options;

  if (!member?.name) return { ok: false, reason: 'no-member' };

  console.log('开始实时轨迹绘制:', member.name, '会话ID:', sessionId);
  const currentMember = getCurrentMember?.();
  let initialPoint = null;
  if (currentMember) {
    initialPoint = buildNormalizedTrackPoint(currentMember);
    if (!initialPoint) {
      console.log(`❌ 队员 ${member.name} 的初始坐标无效`);
      return { ok: false, reason: 'invalid-point' };
    }

    if (sessionId && saveInitialPoint) {
      try {
        await saveInitialPoint(member, initialPoint, sessionId);
      } catch (error) {
        console.error('保存初始轨迹点到数据库失败:', error);
      }
    }

    onInitialPoint?.(member.name, initialPoint);
    setTimeout(() => onDrawLine?.(member.name), 100);
    console.log(`🎯 为队员 ${member.name} 添加高精度初始轨迹点:`, initialPoint);
    console.log(`📍 初始坐标精度: 纬度${initialPoint.lat.toFixed(6)}, 经度${initialPoint.lng.toFixed(6)}`);
  }

  const intervalMs = clampTrackRefreshIntervalMs(refreshIntervalMs);
  const positionWatcher = createRealtimePositionWatcher({
    memberName: member.name,
    sessionId,
    intervalMs,
    getMember,
    onPointAdded,
    savePoint
  });
  member.positionWatcher = positionWatcher;
  console.log(`📡 已为队员 ${member.name} 启动自动轨迹更新，间隔: ${intervalMs / 1000}秒`);
  return { ok: true, initialPoint, positionWatcher, intervalMs };
}

/** 轨迹模式类型是否变化（需用户点「设置」） */
export function resolveTrackModeTypeChange(options = {}) {
  const {
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    settingsLoaded,
    trackModeApplied,
    hydrated = typeof window !== 'undefined' ? window.dashboardSettingsHydrated : false,
    initialized = typeof window !== 'undefined' ? window.dashboardInitialized : false,
    loadTime = typeof window !== 'undefined' ? window.dashboardLoadTime : 0,
    now = Date.now(),
    pageGraceMs = 8000,
    storage = typeof localStorage !== 'undefined' ? localStorage : null
  } = options;

  if (!settingsLoaded || !hydrated) {
    return { action: 'skip' };
  }

  const pageJustLoaded = now - (loadTime || 0) < pageGraceMs;
  if (!initialized || pageJustLoaded) {
    return { action: 'mark-initialized', shouldMark: !initialized };
  }
  if (!trackModeApplied || !storage) {
    return { action: 'skip' };
  }

  const lastAppliedTrackMode = readLastAppliedTrackModeConfig(storage);
  const currentTrackModeConfig = JSON.stringify({ trackMode, trackMinutes, trackHours, trackDays });
  if (lastAppliedTrackMode === currentTrackModeConfig) {
    return { action: 'skip' };
  }

  let lastConfig = null;
  try {
    lastConfig = lastAppliedTrackMode ? JSON.parse(lastAppliedTrackMode) : null;
  } catch (_) {
    lastConfig = null;
  }

  if (lastConfig && lastConfig.trackMode !== trackMode) {
    return { action: 'require-reapply', currentTrackModeConfig };
  }
  return { action: 'skip' };
}

export function buildTrackModeSettingsPayload(trackMode, trackMinutes, trackHours, trackDays, SETTING_KEYS) {
  return {
    [SETTING_KEYS.TRACK_MODE]: trackMode,
    [SETTING_KEYS.TRACK_MINUTES]: trackMinutes,
    [SETTING_KEYS.TRACK_HOURS]: trackHours,
    [SETTING_KEYS.TRACK_DAYS]: trackDays
  };
}
